// Gantt chart print - the full schedule as ONE wide chart (browser Print ->
// Save as PDF). Unlike the schedule report (SchedulePrintPage) nothing is
// squeezed into the page width: every column keeps a readable size and the
// timeline is drawn at a fixed scale (day / week / month), so the sheet grows
// as wide (and as tall) as the schedule needs - the @page size is computed
// from the chart and handed to the browser as a custom paper size.
//
//   /gantt-print/:projectId                      whole project (owner side)
//   /gantt-print/:projectId?company=<id>         one contractor's portion
//   ?scale=day|week|month   timeline density (default week)
//   ?ownerSignOff=signed,planned,not   the schedule chart's owner/GC filter
//   ?autoprint=1            open the print dialog once rendered
//
// Permissions / scope are the server's: the gantt feed itself is scoped to
// the caller's company for partners (supervisors / employees of a
// contractor see their activities + parents for context, their issues and
// checklists only), and the page locks the scope picker to that company.
// Nothing here edits the plan.
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import React, { useEffect, useMemo, useState } from "react";
import { forcedSignOff } from "components/CoordinationGantt/signOffForced";
import { movedSignOff } from "components/CoordinationGantt/signOffMoved";
import { useParams, useLocation } from "react-router-dom";
import { Button, Select, Spin, Checkbox } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { flattenTree } from "components/CoordinationGantt/ganttHelpers";
import { isMilestone } from "helpers/schedule/activityTypes";
import { depId } from "helpers/schedule/predecessors";
import { PRINT_GROUPS, groupRows } from "helpers/schedule/printGroups";
import { issueLabel, prefixOf } from "helpers/artifactPrefix";
import { issueActivityIds } from "helpers/schedule/issueActivityIds";
import { ownerStarDay, ownerStarOn, ownerStarColor, ownerSignOffText, ownerFilterScope, ownerFilterWords, printQuery, OWNER_STAR_BLUE, OWNER_STAR_PLANNED } from "helpers/schedule/ownerSignOffPrint";
import OwnerSignOffFilter from "components/Coordination/OwnerSignOffFilter";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const api = async (path) => { const r = await fetch(`${url}${path}`, { headers: auth() }); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); };
const DAY = 86400000;
const sod = (v) => { const d = new Date(v); d.setHours(0, 0, 0, 0); return d; };
const fmtD = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" }) : "—");
const STATUS = { planned: "Planned", in_progress: "In progress", done: "Done", on_hold: "On hold" };
const TYPE = { task: "Task", milestone: "Milestone", meeting: "Meeting", signoff: "Sign-off", area_release: "Area release" };
const SCALES = { day: { px: 28, label: "Day (widest)" }, week: { px: 8, label: "Week" }, month: { px: 2.2, label: "Month (most compact)" } };
const PX2MM = 0.2646; // CSS px -> mm at 96 dpi
const MAX_MM = 5000;  // browsers cap custom paper sizes around 200 in

// Fixed (left) columns: key, title, width px, cell renderer
const COLS = [
  { key: "wbs", title: "WBS", w: 64 },
  { key: "name", title: "Activity / milestone / meeting", w: 300 },
  { key: "type", title: "Type", w: 78 },
  { key: "status", title: "Status", w: 92 },
  { key: "pct", title: "%", w: 40 },
  { key: "start", title: "Start", w: 84 },
  { key: "end", title: "End", w: 84 },
  { key: "signoff", title: "Sign-off ★", w: 92 },
  { key: "owner", title: "Owner/GC ★", w: 104 },
  { key: "days", title: "Days", w: 44 },
  { key: "company", title: "Responsible / contractor", w: 180 },
  { key: "people", title: "Responsible people", w: 180 },
  { key: "after", title: "After (predecessors)", w: 170 },
  { key: "issues", title: "Issues / actions (open / all)", w: 120 },
  { key: "checks", title: "Checklist (done / all)", w: 110 },
  { key: "health", title: "Health", w: 80 },
];

export default function GanttPrintPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const [company, setCompany] = useState(qs.get("company") || "");
  const [scale, setScale] = useState(SCALES[qs.get("scale")] ? qs.get("scale") : "week");
  const [showContext, setShowContext] = useState(true);
  const [groupBy, setGroupBy] = useState(qs.get("groupBy") || "wbs");
  const [ownerFilter, setOwnerFilter] = useState(qs.get("ownerSignOff") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [title, setTitle] = useState("");

  // Labels for the two axes that group by KEY rather than by a word: an
  // area id and a project-stage key. Both fail quiet - a lane falls back
  // to the raw key, which still groups correctly.
  const [areaNames, setAreaNames] = useState({});
  const [stageLabels, setStageLabels] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { listAreaRows } = require("api/areas/areasApi");
        const rows = await listAreaRows(projectId, "approved");
        if (!alive) return;
        const names = {};
        for (const a of rows) names[String(a._id)] = `${a.code ? `${a.code} · ` : ""}${a.name}`;
        setAreaNames(names);
      } catch { /* ignore */ }
      try {
        const { getTaxonomy } = require("api/taxonomy/taxonomyApi");
        const tax = await getTaxonomy();
        if (!alive) return;
        const labels = {};
        for (const r of tax.project_stage || []) labels[r.key] = r.label;
        setStageLabels(labels);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [feed, checklist, roster, picker] = await Promise.all([
          api(`/projects/${projectId}/gantt`),
          api(`/projects/${projectId}/checklist`).catch(() => []),
          api(`/projects/${projectId}/participants/roster`).catch(() => []),
          api(`/projects/picker`).catch(() => ({ projects: [] })),
        ]);
        if (!alive) return;
        const p = (picker.projects || picker || []).find((x) => String(x._id) === String(projectId));
        setTitle(p ? p.title : (feed.projectTitle || ""));
        // Names the PDF the browser saves: "SPX-gantt-2026-08-20.pdf".
        const pfx = feed.artifactPrefix || prefixOf(p);
        try {
          document.title = `${pfx}-gantt-${new Date().toISOString().slice(0, 10)}`;
        } catch (e) { /* title is cosmetic */ }

        const access = feed.access || {};
        // Partners are locked to their own company - the feed is already
        // scoped server-side; the picker just reflects it.
        if (access.partnerCompanyId) setCompany(access.partnerCompanyId);
        setData({ items: feed.items || [], issues: feed.issues || [], checklist: Array.isArray(checklist) ? checklist : [], roster: Array.isArray(roster) ? roster : [], health: feed.health || {}, access });
      } catch (e) { if (alive) setErr(e.message || "Could not load the schedule"); }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const chart = useMemo(() => {
    if (!data) return null;
    const { items, issues, checklist, roster } = data;
    const nameOf = new Map(roster.map((g) => [String(g.companyId), g.companyName]));
    const byId = new Map(items.map((i) => [String(i._id), i]));
    let scopeIds = null;
    if (company) {
      scopeIds = new Set();
      for (const it of items) if (String(it.responsibleCompanyId || "") === String(company)) scopeIds.add(String(it._id));
      for (const it of items) {
        if (!scopeIds.has(String(it._id))) continue;
        let p = it.parentId ? byId.get(String(it.parentId)) : null;
        while (p && !scopeIds.has(String(p._id))) { scopeIds.add(String(p._id)); p = p.parentId ? byId.get(String(p.parentId)) : null; }
      }
    }
    let visible = scopeIds ? items.filter((i) => scopeIds.has(String(i._id))) : items;
    // Owner/GC filter: on what the scope left, so a parent kept only for
    // the filter prints as context, like a contractor's parents.
    const owner = ownerFilterScope(visible, ownerFilter);
    if (owner) visible = visible.filter((i) => owner.keep.has(String(i._id)));
    const isOwn = (it) => !(it.context) && (!scopeIds || String(it.responsibleCompanyId || "") === String(company)) && !(owner && owner.context.has(String(it._id)));
    if (!showContext) visible = visible.filter(isOwn);
    const flat = flattenTree(visible).map((r) => ({ ...r, own: isOwn(r.item) }));
    const rows = groupRows(flat, groupBy, {
      areas: areaNames,
      stages: stageLabels,
      companies: Object.fromEntries((data.roster || []).map((g) => [String(g.companyId), g.companyName])),
    });
    const issuesByItem = new Map();
    for (const i of issues) {
      if (company && String(i.responsibleCompanyId || "") !== String(company)) continue;
      // Under every activity it is on (several, all equal).
      const keys = issueActivityIds(i);
      for (const k of keys.length ? keys : [""]) {
        if (!issuesByItem.has(k)) issuesByItem.set(k, []);
        issuesByItem.get(k).push(i);
      }
    }
    const companyEmails = new Set((roster.find((g) => String(g.companyId) === String(company))?.users || []).map((u) => String(u.email || "").toLowerCase()));
    const checksByItem = new Map();
    for (const c of checklist) {
      if (company && !(String(c.responsibleCompanyId || "") === String(company) || companyEmails.has(String(c.currentAssignee?.email || "").toLowerCase()) || scopeIds.has(String((c.ganttItemId && c.ganttItemId._id) || c.ganttItemId || "")))) continue;
      const k = String((c.ganttItemId && c.ganttItemId._id) || c.ganttItemId || "");
      if (!checksByItem.has(k)) checksByItem.set(k, []);
      checksByItem.get(k).push(c);
    }
    // The client's blue star stays on the sheet even when its day is past the last bar.
    const dated = visible.flatMap((i) => [i.startDate, i.endDate, ownerStarOn(i) ? ownerStarDay(i) : null]).filter(Boolean).map((x) => +sod(x));
    const today = +sod(new Date());
    let t0 = dated.length ? Math.min(...dated) : today, t1 = dated.length ? Math.max(...dated) : today + 30 * DAY;
    // A little margin on both sides, and always include today.
    t0 = Math.min(t0, today) - 3 * DAY; t1 = Math.max(t1, today) + 4 * DAY;
    const nDays = Math.max(1, Math.round((t1 - t0) / DAY));
    return { rows, byId, issuesByItem, checksByItem, nameOf, t0, t1, nDays, today };
  }, [data, company, showContext, ownerFilter]);

  useEffect(() => {
    if (chart && qs.get("autoprint") === "1") { const t = setTimeout(() => window.print(), 900); return () => clearTimeout(t); }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  if (err) return <div style={{ padding: 24 }}>{err}</div>;
  if (!chart) return <div style={{ padding: 40, textAlign: "center" }}><Spin /> <div style={{ color: "#888", marginTop: 8 }}>Preparing the Gantt chart…</div></div>;

  const { rows, byId, issuesByItem, checksByItem, nameOf, t0, t1, nDays, today } = chart;
  const px = SCALES[scale].px;
  const ROW_H = 22, HEAD_H = 40;
  // Paper cannot scroll a clipped cell: when any sign-off was moved, the
  // column is wide enough for "☆ Sep 1, 26 (was Aug 27, 26)".
  const cols = rows.some((r) => r.item && movedSignOff(r.item)) ? COLS.map((c) => (c.key === "signoff" ? { ...c, w: 172 } : c)) : COLS;
  const leftW = cols.reduce((a, c) => a + c.w, 0);
  const timeW = Math.ceil(nDays * px);
  const totalW = leftW + timeW;
  const totalH = HEAD_H + rows.length * ROW_H;
  // Custom paper size: the chart plus the header block and margins.
  const pageWmm = Math.min(MAX_MM, Math.ceil(totalW * PX2MM) + 24);
  const pageHmm = Math.min(MAX_MM, Math.ceil((totalH + 90) * PX2MM) + 24);
  const x = (t) => ((+sod(t) - t0) / DAY) * px;
  const locked = !!data.access?.partnerCompanyId;
  const companyName = company ? (nameOf.get(String(company)) || "Contractor") : "";
  const barColor = (it) => (it.disabled ? "#bbb" : it.status === "done" ? "#2e7d32" : it.healthStatus === "behind" ? "#c62828" : it.healthStatus === "at_risk" ? "#ef8f00" : it.status === "in_progress" ? "#1976d2" : "#8a94a6");
  const personsOf = (it) => (it.responsibleUsers && it.responsibleUsers.length ? it.responsibleUsers.map((u) => u.name || u.email).filter(Boolean).join(", ") : it.responsibleUserName || it.responsibleUserEmail || "");

  // Time axis: month bands + (week | day) ticks
  const months = [];
  { let d = new Date(t0); d.setDate(1); while (+d < t1) { const s = Math.max(+d, t0); const e = new Date(d); e.setMonth(e.getMonth() + 1); months.push({ label: d.toLocaleDateString(undefined, { month: "short", year: "numeric" }), x: x(s), w: x(Math.min(+e, t1)) - x(s) }); d = e; } }
  const ticks = [];
  if (scale === "day") { for (let t = t0; t < t1; t += DAY) { const d = new Date(t); ticks.push({ x: x(t), w: px, label: String(d.getDate()), weekend: d.getDay() === 0 || d.getDay() === 6 }); } }
  else if (scale === "week") { let d = new Date(t0); while (d.getDay() !== 1) d = new Date(+d + DAY); for (let t = +d; t < t1; t += 7 * DAY) ticks.push({ x: x(t), w: 7 * px, label: `${new Date(t).getDate()}` }); }

  const cell = (c, it, depth, own) => {
    switch (c.key) {
      case "wbs": return it.wbs || "";
      case "name": return <span style={{ paddingLeft: depth * 12, fontWeight: depth === 0 ? 600 : 400, textDecoration: it.disabled ? "line-through" : "none" }}>{it.name}</span>;
      case "type": return TYPE[it.type] || it.type;
      case "status": return it.disabled ? "Disabled" : STATUS[it.status] || it.status;
      case "pct": return it.status === "done" ? "100" : it.progress != null ? String(Math.round(it.progress)) : "";
      case "start": return fmtD(it.startDate);
      case "end": return fmtD(it.endDate);
      // PAPER CANNOT HOVER, so an overridden sign-off says so in the
      // cell rather than in a title nobody will ever see. The word alone
      // fits a print column; the reasons live in the detail panel and
      // the shop-drawing register.
      case "signoff": return it.signOffDate
        ? `${it.signedOffAt ? `★ ${fmtD(it.signedOffAt)}${forcedSignOff(it) ? " (overridden)" : ""}` : `☆ ${fmtD(it.signOffDate)}`}${movedSignOff(it) ? ` (was ${fmtD(movedSignOff(it))})` : ""}`
        : "";
      case "owner": return ownerSignOffText(it, fmtD);
      case "days": return it.startDate && it.endDate && !isMilestone(it.type) ? String(Math.max(1, Math.round((+sod(it.endDate) - +sod(it.startDate)) / DAY) + 1)) : "";
      case "company": return nameOf.get(String(it.responsibleCompanyId)) || (it.contractorLabel ? `${it.contractorLabel} (label)` : "");
      case "people": return personsOf(it);
      case "after": return (it.dependencies || []).map((dep) => byId.get(depId(dep))).filter(Boolean).map((p) => p.wbs || p.name).join(", ");
      case "issues": { const l = issuesByItem.get(String(it._id)) || []; return l.length ? `${l.filter((i) => ["open", "pending", "under_review"].includes(i.status)).length} / ${l.length}` : ""; }
      case "checks": { const l = checksByItem.get(String(it._id)) || []; return l.length ? `${l.filter((i) => i.status === "complete").length} / ${l.length}` : ""; }
      case "health": return it.disabled ? "" : (it.healthStatus || "").replace("_", " ");
      default: return "";
    }
  };

  return (
    <div className="gantt-print" style={{ fontFamily: "Inter, Arial, sans-serif", color: "#222", padding: "12px 16px", width: totalW + 32 }}>
      <style>{`
        @page { size: ${pageWmm}mm ${pageHmm}mm; margin: 10mm; }
        @media print {
          body * { visibility: hidden !important; }
          .gantt-print, .gantt-print * { visibility: visible !important; }
          .gantt-print { position: absolute !important; left: 0; top: 0; padding: 0; margin: 0; }
          .no-print { display: none !important; }
          html, body { zoom: 1 !important; width: auto !important; overflow: visible !important; }
        }
        .gantt-print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .gantt-print .grid { display: grid; font-size: 10.5px; border: 1px solid #d0d3d9; border-radius: 4px; overflow: hidden; }
        .gantt-print .hc { background: #f1f3f6; font-weight: 600; border-right: 1px solid #d9dce2; border-bottom: 1px solid #c9cdd5; padding: 4px 5px; display: flex; align-items: flex-end; white-space: nowrap; overflow: hidden; }
        .gantt-print .c { border-right: 1px solid #eceef2; border-bottom: 1px solid #eceef2; padding: 0 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; }
        .gantt-print .ctx { color: #999; }
        .gantt-print .ctx .bar { opacity: .35; }
        .gantt-print .parent { background: #fafbfc; }
      `}</style>

      <div className="no-print" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Print / Save as PDF</Button>
        <span style={{ fontSize: 12, color: "#777" }}>Scope:</span>
        {locked ? (
          <b style={{ fontSize: 12 }}>{companyName || "Your company"} (your portion of the schedule)</b>
        ) : (
          <Select style={{ minWidth: 260 }} value={company || ""} onChange={(v) => setCompany(v || "")}
            options={[{ value: "", label: "Whole project (every contractor)" }, ...data.roster.filter((g) => g.role !== "owner").map((g) => ({ value: String(g.companyId), text: `${g.companyName} only`, label: <PartnerWithTrades name={`${g.companyName} only`} trades={(g.tradeLabels && g.tradeLabels.length ? g.tradeLabels : g.trades) || []} /> }))]}
            optionLabelProp="text" />
        )}
        <span style={{ fontSize: 12, color: "#777" }}>Timeline scale:</span>
        <Select style={{ width: 190 }} value={scale} onChange={setScale} options={Object.keys(SCALES).map((k) => ({ value: k, label: SCALES[k].label }))} />
        <OwnerSignOffFilter width={200} value={ownerFilter} onChange={setOwnerFilter} />
        {(company || locked || ownerFilter) && <Checkbox checked={showContext} onChange={(e) => setShowContext(e.target.checked)}>Show parent activities for context</Checkbox>}
        <span style={{ fontSize: 12, color: "#777" }}>
          Sheet {pageWmm} × {pageHmm} mm ({rows.length} rows · {nDays} days). In the print dialog keep "Default" paper size (the page size is set by the chart) and turn background graphics on.
        </span>
        <span style={{ fontSize: 12, color: "#777" }}>Group by:</span>
        <Select style={{ width: 160 }} value={groupBy} onChange={setGroupBy} options={PRINT_GROUPS} />
        <a href={`/schedule-print/${projectId}${printQuery(company, ownerFilter)}`} style={{ fontSize: 12 }}>Switch to the detailed schedule report</a>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 18 }}>{title || "Schedule"} — Gantt chart{companyName ? ` · ${companyName}` : ""}</h1>
        <div style={{ fontSize: 11, color: "#777" }}>
          Generated {new Date().toLocaleString()} · {rows.filter((r) => r.own).length} rows{company || ownerFilter ? ` (${rows.filter((r) => !r.own).length} parent rows for context)` : ""}{ownerFilter ? ` · owner/GC sign-off: ${ownerFilterWords(ownerFilter)}` : ""} · {fmtD(t0)} – {fmtD(t1)}
          {data.health?.status ? ` · health: ${data.health.status}` : ""}
        </div>
      </div>

      {ownerFilter && !rows.some((r) => r.own) && (
        <div data-testid="print-owner-filter-empty" style={{ fontSize: 12, color: "#777", margin: "4px 0 8px" }}>
          No activities in that owner/GC sign-off state ({ownerFilterWords(ownerFilter)}).
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: `${cols.map((c) => `${c.w}px`).join(" ")} ${timeW}px`, width: totalW }}>
        {/* header */}
        {cols.map((c) => <div key={c.key} className="hc" style={{ height: HEAD_H }}>{c.title}</div>)}
        <div style={{ position: "relative", height: HEAD_H, borderBottom: "1px solid #c9cdd5", background: "#f1f3f6" }}>
          {months.map((m, i) => <div key={i} style={{ position: "absolute", left: m.x, width: m.w, top: 0, height: 20, borderRight: "1px solid #c9cdd5", fontSize: 10.5, fontWeight: 600, padding: "3px 4px", whiteSpace: "nowrap", overflow: "hidden" }}>{m.w > 30 ? m.label : ""}</div>)}
          {ticks.map((t, i) => <div key={i} style={{ position: "absolute", left: t.x, width: t.w, top: 20, height: 20, borderRight: "1px solid #e2e5ea", fontSize: 9, color: t.weekend ? "#b5b5b5" : "#555", padding: "4px 2px", textAlign: "center", background: t.weekend ? "#f7f7f7" : "transparent" }}>{t.w > 14 ? t.label : ""}</div>)}
          {today >= t0 && today <= t1 && <div style={{ position: "absolute", left: x(today), top: 0, bottom: 0, borderLeft: "2px solid #c62828" }} />}
        </div>
        {/* rows */}
        {rows.map(({ item, depth, own, hasChildren, groupKey, groupLabel }) => {
          // A header row from groupRows(): the label sits in the first
          // column and the rest of the grid row is left empty, so the
          // timeline stays aligned.
          if (!item) return (
            <React.Fragment key={groupKey}>
              {cols.map((c, i) => (
                <div key={c.key} className="c grp" style={{ height: ROW_H, fontWeight: 700, background: "#f4f6f8" }}>
                  {i === 0 ? groupLabel : ""}
                </div>
              ))}
              <div className="c grp" style={{ height: ROW_H, background: "#f4f6f8" }} />
            </React.Fragment>
          );
          const cls = `${own ? "" : "ctx"} ${hasChildren ? "parent" : ""}`;
          const hasDates = item.startDate && item.endDate;
          const left = hasDates ? x(item.startDate) : 0;
          const w = hasDates ? Math.max(px, x(+sod(item.endDate) + DAY) - left) : 0;
          const prog = item.status === "done" ? 100 : Math.max(0, Math.min(100, item.progress || 0));
          const iss = issuesByItem.get(String(item._id)) || [];
          return (
            <React.Fragment key={item._id}>
              {cols.map((c) => <div key={c.key} className={`c ${cls}`} style={{ height: ROW_H }} title={c.key === "name" ? item.name : undefined}>{cell(c, item, depth, own)}</div>)}
              <div className={`c ${cls}`} style={{ height: ROW_H, position: "relative", padding: 0, overflow: "hidden" }}>
                {ticks.map((t, i) => <div key={i} style={{ position: "absolute", left: t.x, width: t.w, top: 0, bottom: 0, borderRight: "1px solid #f1f2f5", background: t.weekend ? "#fafafa" : "transparent" }} />)}
                {months.map((m, i) => <div key={`m${i}`} style={{ position: "absolute", left: m.x + m.w - 1, top: 0, bottom: 0, borderRight: "1px solid #e6e8ec" }} />)}
                {hasDates && isMilestone(item.type) ? (
                  <div className="bar" style={{ position: "absolute", left: left - 6 + px / 2, top: ROW_H / 2 - 6, width: 12, height: 12, background: barColor(item), transform: "rotate(45deg)" }} />
                ) : hasDates ? (
                  <div className="bar" style={{ position: "absolute", left, width: w, top: hasChildren ? 7 : 5, height: hasChildren ? 8 : 12, background: barColor(item), borderRadius: 2, opacity: hasChildren ? 0.55 : 1 }}>
                    {prog > 0 && prog < 100 && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${prog}%`, background: "rgba(0,0,0,.28)", borderRadius: 2 }} />}
                  </div>
                ) : null}
                {hasDates && w > 60 && !hasChildren && (
                  <div style={{ position: "absolute", left: left + 4, top: 4, fontSize: 9.5, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", maxWidth: w - 8, textOverflow: "ellipsis", lineHeight: "14px" }}>
                    {prog ? `${Math.round(prog)}%` : ""}{iss.length ? ` · ${iss.filter((i) => ["open", "pending", "under_review"].includes(i.status)).length}/${iss.length} issues` : ""}
                  </div>
                )}
                {/* issue due markers */}
                {iss.filter((i) => i.dueDate).map((i) => (
                  <div key={i._id} title={`${i.displayId ? `${issueLabel(i)} ` : ""}${i.title}`} style={{ position: "absolute", left: x(i.dueDate) + px / 2 - 3, bottom: 1, width: 6, height: 6, borderRadius: "50%", background: ["open", "pending", "under_review"].includes(i.status) ? (+sod(i.dueDate) < today ? "#c62828" : "#ef8f00") : "#2e7d32" }} />
                ))}
                {/* first planned sign-off of a moved one: grey star, drawn under the live one */}
                {movedSignOff(item) && +sod(movedSignOff(item)) >= t0 && +sod(movedSignOff(item)) <= t1 && (
                  <div data-testid="print-signoff-original" title={`Sign-off originally planned ${fmtD(movedSignOff(item))} - moved to ${fmtD(item.signOffDate)}`}
                       style={{ position: "absolute", left: x(movedSignOff(item)) + px / 2 - 7, top: ROW_H / 2 - 9, fontSize: 14, lineHeight: "18px", color: "#b0b0b0" }}>★</div>
                )}
                {/* sign-off star: gold pending, green signed, red overdue */}
                {item.signOffDate && +sod(item.signOffDate) >= t0 && +sod(item.signOffDate) <= t1 && (
                  <div title={item.signedOffAt ? `Signed off ${fmtD(item.signedOffAt)}${forcedSignOff(item) ? ` · ${forcedSignOff(item).short}` : ""}` : `Sign-off due ${fmtD(item.signOffDate)}`}
                       style={{ position: "absolute", left: x(item.signOffDate) + px / 2 - 7, top: ROW_H / 2 - 9, fontSize: 14, lineHeight: "18px", color: item.signedOffAt ? "#2e7d32" : +sod(item.signOffDate) < today ? "#c62828" : "#d9a400" }}>★</div>
                )}
                {/* the client's sign-off the GC marked: blue (pale = planned), moved
                    right when it shares a day with the sign-off star so both show */}
                {ownerStarOn(item) && +sod(ownerStarDay(item)) >= t0 && +sod(ownerStarDay(item)) <= t1 && (
                  <div data-testid="print-owner-signoff" title={ownerSignOffText(item, fmtD, { long: true })}
                       style={{ position: "absolute", left: x(ownerStarDay(item)) + px / 2 - 7 + (item.signOffDate && +sod(item.signOffDate) === +sod(ownerStarDay(item)) ? 12 : 0), top: ROW_H / 2 - 9, fontSize: 14, lineHeight: "18px", color: ownerStarColor(item) }}>★</div>
                )}
                {today >= t0 && today <= t1 && <div style={{ position: "absolute", left: x(today), top: 0, bottom: 0, borderLeft: "1px dashed #c62828" }} />}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ fontSize: 10, color: "#777", marginTop: 6 }}>
        Bars: <span style={{ color: "#1976d2" }}>■</span> in progress · <span style={{ color: "#8a94a6" }}>■</span> planned · <span style={{ color: "#2e7d32" }}>■</span> done · <span style={{ color: "#ef8f00" }}>■</span> at risk · <span style={{ color: "#c62828" }}>■</span> behind · <span style={{ color: "#bbb" }}>■</span> disabled · darker fill = progress % · ◆ milestone · ● issue due (red = overdue, orange = open, green = resolved) · ★ sign-off date (gold = pending, green = signed off, red = overdue, grey = where a moved sign-off was first planned) · <span style={{ color: OWNER_STAR_BLUE }}>★</span> signed off by owner/GC (<span style={{ color: OWNER_STAR_PLANNED }}>★</span> planned; ↳ in the column = covered by the row above) · red line = today{company || ownerFilter ? " · grey rows = parent activities shown for context only" : ""}
      </div>
    </div>
  );
}
